import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FiCode,
  FiCopy,
  FiDownload,
  FiServer,
  FiCalendar,
  FiLayers,
  FiCheckCircle,
  FiUploadCloud,
  FiFolder,
  FiFile,
  FiX,
  FiChevronRight,
  FiExternalLink,
  FiArrowUp,
  FiRefreshCw,
} from 'react-icons/fi';

/** Matches server `eventTypes` list (FLabs script generator). */
const EVENT_OPTIONS = [
  {
    value: 'CREATE_TABLE',
    label: 'CREATE TABLE',
    hint: 'New table definitions (IF NOT EXISTS).',
  },
  {
    value: 'ALTER_TABLE',
    label: 'ALTER TABLE',
    hint: 'Column and table changes.',
  },
  {
    value: 'CREATE_PROCEDURE',
    label: 'CREATE PROCEDURE',
    hint: 'New or replace procedures.',
  },
  {
    value: 'ALTER_PROCEDURE',
    label: 'ALTER PROCEDURE',
    hint: 'Procedure alterations.',
  },
  {
    value: 'CREATE_TRIGGER',
    label: 'CREATE TRIGGER',
    hint: 'New triggers.',
  },
  {
    value: 'ALTER_TRIGGER',
    label: 'ALTER TRIGGER',
    hint: 'Trigger alterations.',
  },
  {
    value: 'CREATE_SCHEMA',
    label: 'CREATE SCHEMA',
    hint: 'Schema objects.',
  },
];

function createAllTrueOutputFilter() {
  return Object.fromEntries(EVENT_OPTIONS.map((o) => [o.value, true]));
}

function proxyBase() {
  const explicit = process.env.REACT_APP_PATCH_DISPATCH_URL;
  if (explicit) return String(explicit).replace(/\/$/, '');
  const port = process.env.REACT_APP_PATCH_SERVER_PORT || '8787';
  return `http://127.0.0.1:${port}`;
}

/** Initial prefix for Desk blob browse (e.g. patch drops). Override with REACT_APP_DESK_BLOB_DEFAULT_PREFIX. */
function defaultBlobBrowsePrefix() {
  const raw = process.env.REACT_APP_DESK_BLOB_DEFAULT_PREFIX;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return 'patches/';
}

/** Desk list response may be wrapped as { success, data: { directories, files, … } }. */
function unwrapBlobBrowseBody(body) {
  if (!body || typeof body !== 'object') return null;
  const inner = body.data;
  if (inner && typeof inner === 'object' && (Array.isArray(inner.directories) || Array.isArray(inner.files))) {
    return inner;
  }
  if (Array.isArray(body.directories) || Array.isArray(body.files)) return body;
  return null;
}

/** Parent virtual folder prefix, or undefined if already at container root (cannot go up). */
function blobParentPrefix(normalizedPrefix) {
  if (normalizedPrefix == null || normalizedPrefix === '') return undefined;
  const t = String(normalizedPrefix).replace(/\/+$/, '');
  const i = t.lastIndexOf('/');
  if (i <= 0) return '';
  return `${t.slice(0, i + 1)}`;
}

function deskSuccessMessage(body, fallback = 'Done.') {
  if (!body || typeof body !== 'object') return fallback;
  return (
    body.message ||
    body.Message ||
    (body.data && typeof body.data === 'object' && (body.data.message || body.data.Message)) ||
    fallback
  );
}

/** Matches lines like `-- ========== ALTER_TABLE Scripts ==========` */
const SECTION_HEADER_RE = /^--\s*=+\s*([A-Z_]+)\s+Scripts\s*=+\s*$/;

/**
 * Split generated script into named sections (CREATE_TABLE, ALTER_TABLE, CREATE_PROCEDURE, …).
 * If no headers found, returns empty sections and caller should show raw text.
 */
function parseSqlSections(text) {
  if (!text || !String(text).trim()) return { sections: [] };
  const lines = text.split(/\r?\n/).map((l) => l.replace(/\r$/, ''));
  const sections = [];
  let i = 0;
  while (i < lines.length && !SECTION_HEADER_RE.test(lines[i].trim())) i++;
  if (i >= lines.length) return { sections: [] };
  while (i < lines.length) {
    const m = lines[i].trim().match(SECTION_HEADER_RE);
    if (!m) {
      i++;
      continue;
    }
    const key = m[1];
    const start = i;
    i++;
    while (i < lines.length && !SECTION_HEADER_RE.test(lines[i].trim())) i++;
    sections.push({ key, text: lines.slice(start, i).join('\n') });
  }
  return { sections };
}

function keysPresentInScript(sections) {
  return [...new Set(sections.map((s) => s.key))];
}

export default function SqlScriptGenerator() {
  const base = useMemo(() => proxyBase(), []);
  const [apiReady, setApiReady] = useState(null);
  const [filterDate, setFilterDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [uploadedToCloud, setUploadedToCloud] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(() =>
    EVENT_OPTIONS.map((o) => o.value),
  );
  const [submitting, setSubmitting] = useState(false);
  const [sqlText, setSqlText] = useState('');
  /** Which section types to show in the output panel (display-only filter). */
  const [outputSectionFilter, setOutputSectionFilter] = useState(createAllTrueOutputFilter);
  const { displayedSql, parsedSections, keysInFile, showingFullScript } = useMemo(() => {
    if (!sqlText) {
      return {
        displayedSql: '',
        parsedSections: [],
        keysInFile: [],
        showingFullScript: true,
      };
    }
    const { sections } = parseSqlSections(sqlText);
    if (sections.length === 0) {
      return {
        displayedSql: sqlText,
        parsedSections: [],
        keysInFile: [],
        showingFullScript: true,
      };
    }
    const present = keysPresentInScript(sections);
    const allOn =
      present.length > 0 &&
      present.every((k) => (k in outputSectionFilter ? outputSectionFilter[k] : true));
    if (allOn) {
      return {
        displayedSql: sqlText,
        parsedSections: sections,
        keysInFile: present,
        showingFullScript: true,
      };
    }
    const parts = sections
      .filter((s) => (s.key in outputSectionFilter ? outputSectionFilter[s.key] : true))
      .map((s) => s.text);
    return {
      displayedSql: parts.join('\n\n'),
      parsedSections: sections,
      keysInFile: present,
      showingFullScript: false,
    };
  }, [sqlText, outputSectionFilter]);

  /** User-editable buffer; reset when the filtered view (`displayedSql`) changes. */
  const [editableOutput, setEditableOutput] = useState('');

  const [uploadPickerOpen, setUploadPickerOpen] = useState(false);
  const [blobBrowsePrefix, setBlobBrowsePrefix] = useState(() => defaultBlobBrowsePrefix());
  const [blobBrowseLoading, setBlobBrowseLoading] = useState(false);
  const [blobBrowseError, setBlobBrowseError] = useState(null);
  const [blobBrowsePayload, setBlobBrowsePayload] = useState(null);
  const [blobUploading, setBlobUploading] = useState(false);
  const [extraUploadFiles, setExtraUploadFiles] = useState([]);
  const [includeScriptInUpload, setIncludeScriptInUpload] = useState(true);
  const blobExtraFilesInputRef = useRef(null);

  useEffect(() => {
    setEditableOutput(displayedSql);
  }, [displayedSql]);

  const displayedLineCount = editableOutput ? editableOutput.split('\n').length : 0;
  const isEdited =
    Boolean(sqlText) && displayedSql !== '' && editableOutput !== displayedSql;

  const checkHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/api/health`, { timeout: 8000 });
      setApiReady(Boolean(data?.ok));
    } catch {
      setApiReady(false);
    }
  }, [base]);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  const loadBlobBrowse = useCallback(
    async (prefix) => {
      setBlobBrowseLoading(true);
      setBlobBrowseError(null);
      setBlobBrowsePayload(null);
      try {
        const { data } = await axios.get(`${base}/api/desk/storage/blob/browse`, {
          params: { prefix },
          timeout: 60000,
          validateStatus: () => true,
        });
        if (!data || typeof data !== 'object') {
          setBlobBrowseError('Unexpected response from patch server.');
          return;
        }
        if (data.error) {
          setBlobBrowseError(typeof data.error === 'string' ? data.error : 'Browse failed.');
          return;
        }
        const payload = unwrapBlobBrowseBody(data);
        if (!payload) {
          setBlobBrowseError('Could not read directory listing (unexpected JSON shape).');
          return;
        }
        setBlobBrowsePayload(payload);
        if (typeof payload.prefix === 'string') setBlobBrowsePrefix(payload.prefix);
      } catch (e) {
        setBlobBrowseError(e.message || 'Network error');
      } finally {
        setBlobBrowseLoading(false);
      }
    },
    [base],
  );

  useEffect(() => {
    if (!uploadPickerOpen) return;
    setExtraUploadFiles([]);
    setIncludeScriptInUpload(true);
    if (blobExtraFilesInputRef.current) blobExtraFilesInputRef.current.value = '';
    const initial = defaultBlobBrowsePrefix();
    setBlobBrowsePrefix(initial);
    loadBlobBrowse(initial);
  }, [uploadPickerOpen, loadBlobBrowse]);

  const openUploadPicker = () => {
    setUploadPickerOpen(true);
  };

  const closeUploadPicker = () => {
    setUploadPickerOpen(false);
    setBlobBrowseError(null);
    setBlobBrowsePayload(null);
    setExtraUploadFiles([]);
    if (blobExtraFilesInputRef.current) blobExtraFilesInputRef.current.value = '';
  };

  const browsePrefixForNav =
    blobBrowsePayload != null && typeof blobBrowsePayload.prefix === 'string'
      ? blobBrowsePayload.prefix
      : blobBrowsePrefix;

  const parentBrowsePrefix = blobParentPrefix(browsePrefixForNav);

  const hasBlobUploadPayload =
    Boolean(includeScriptInUpload && editableOutput.trim()) || extraUploadFiles.length > 0;

  const onBlobUpload = async () => {
    if (!blobBrowsePayload) {
      toast.error('Open a folder listing first (wait for load or fix errors).', { transition: Slide });
      return;
    }
    const prefix = browsePrefixForNav;
    const includeScript = includeScriptInUpload && Boolean(editableOutput.trim());
    const extras = extraUploadFiles;
    if (!includeScript && extras.length === 0) {
      toast.error('Include the generated script and/or add at least one file.', { transition: Slide });
      return;
    }
    if (includeScriptInUpload && !editableOutput.trim()) {
      toast.error('Output is empty — uncheck “Include generated .sql” or paste SQL.', { transition: Slide });
      return;
    }

    const fd = new FormData();
    fd.append('prefix', prefix);
    if (includeScript) {
      fd.append(
        'files',
        new Blob([editableOutput], { type: 'text/plain;charset=utf-8' }),
        'patch.sql',
      );
    }
    for (let i = 0; i < extras.length; i++) {
      fd.append('files', extras[i], extras[i].name);
    }

    setBlobUploading(true);
    try {
      const res = await axios.post(`${base}/api/desk/storage/blob/upload`, fd, {
        timeout: 300000,
        validateStatus: () => true,
      });
      if (res.status < 200 || res.status >= 300) {
        const msg =
          (res.data && typeof res.data === 'object' && (res.data.error || res.data.message)) ||
          (typeof res.data === 'string' ? res.data.slice(0, 400) : null) ||
          `Upload failed (HTTP ${res.status})`;
        toast.error(String(msg), { transition: Slide, autoClose: 8000 });
        return;
      }
      toast.success(deskSuccessMessage(res.data, 'Uploaded.'), { transition: Slide });
      setExtraUploadFiles([]);
      if (blobExtraFilesInputRef.current) blobExtraFilesInputRef.current.value = '';
      await loadBlobBrowse(prefix);
    } catch (e) {
      toast.error(e.message || 'Upload failed', { transition: Slide });
    } finally {
      setBlobUploading(false);
    }
  };

  const toggleType = (value) => {
    setSelectedTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const selectAllTypes = () => setSelectedTypes(EVENT_OPTIONS.map((o) => o.value));

  const onGenerate = async () => {
    if (!filterDate.trim()) {
      toast.error('Choose a filter date.', { transition: Slide });
      return;
    }
    if (selectedTypes.length === 0) {
      toast.error('Select at least one script category.', { transition: Slide });
      return;
    }
    setSubmitting(true);
    setSqlText('');
    try {
      const res = await axios.post(
        `${base}/api/script-generator/generate`,
        {
          filterDate: filterDate.trim(),
          uploadedToCloud,
          eventTypes: selectedTypes,
        },
        {
          responseType: 'text',
          transformResponse: [(d) => d],
          timeout: 300000,
          validateStatus: () => true,
        },
      );
      if (res.status < 200 || res.status >= 300) {
        let msg = `Request failed (HTTP ${res.status})`;
        try {
          const j = JSON.parse(res.data);
          if (j.error) msg = j.error;
        } catch {
          if (typeof res.data === 'string' && res.data) msg = res.data.slice(0, 400);
        }
        toast.error(msg, { transition: Slide, autoClose: 8000 });
        return;
      }
      const text = typeof res.data === 'string' ? res.data : String(res.data ?? '');
      setSqlText(text);
      setOutputSectionFilter(createAllTrueOutputFilter());
      const lines = text ? text.split('\n').length : 0;
      toast.success(
        <div className="text-left">
          <p className="font-semibold text-emerald-950">Script generated</p>
          <p className="text-sm text-emerald-900/90">
            {lines.toLocaleString()} lines · {(text.length / 1024).toFixed(1)} KB
          </p>
        </div>,
        {
          transition: Slide,
          className:
            '!rounded-xl !bg-gradient-to-r !from-emerald-50 !to-teal-50 !border !border-emerald-200/90',
          bodyClassName: '!p-3',
        },
      );
    } catch (e) {
      toast.error(e.message || 'Network error', { transition: Slide });
    } finally {
      setSubmitting(false);
    }
  };

  const onCopy = async () => {
    if (!sqlText) return;
    try {
      await navigator.clipboard.writeText(editableOutput);
      toast.success('Copied to clipboard', { transition: Slide });
    } catch {
      toast.error('Could not copy', { transition: Slide });
    }
  };

  const onDownload = () => {
    if (!sqlText || !editableOutput.trim()) return;
    const suffix =
      `${showingFullScript ? '' : '-filtered'}${isEdited ? '-edited' : ''}`;
    const blob = new Blob([editableOutput], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `migration-${filterDate.trim()}${suffix}.sql`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Download started', { transition: Slide });
  };

  const fullLineCount = sqlText ? sqlText.split('\n').length : 0;

  const sortedBlobDirs = useMemo(() => {
    const d = blobBrowsePayload?.directories;
    if (!Array.isArray(d)) return [];
    return [...d].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );
  }, [blobBrowsePayload]);

  const sortedBlobFiles = useMemo(() => {
    const f = blobBrowsePayload?.files;
    if (!Array.isArray(f)) return [];
    return [...f].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );
  }, [blobBrowsePayload]);

  const toggleOutputSection = (key) => {
    setOutputSectionFilter((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="box-border min-h-full w-full min-w-0 max-w-full bg-gradient-to-br from-slate-50 via-cyan-50/30 to-indigo-100/40 px-4 py-4 md:px-6 md:py-6">
      <ToastContainer position="top-right" autoClose={3800} pauseOnHover closeOnClick theme="light" />

      <div className="mx-auto w-full min-w-0 max-w-full space-y-6">
        <header className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-cyan-700/90">
            Database
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            SQL script generator
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-slate-600">
            Build a combined migration script from FLabs change history. Requests go through the local patch server
            at{' '}
            <span className="font-mono text-xs text-slate-700">{base}</span>.
          </p>
        </header>

        {apiReady === null && (
          <p className="text-xs text-slate-500">Checking patch server…</p>
        )}

        {apiReady === false && (
          <div
            role="status"
            className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm"
          >
            <FiServer className="mt-0.5 shrink-0 text-lg text-amber-700" />
            <div>
              <p className="font-semibold">Patch server not reachable</p>
              <p className="mt-1 text-amber-900/85">
                Start the proxy from the project root:{' '}
                <code className="rounded bg-amber-100/80 px-1 py-0.5 text-xs">npm run server</code>
                (default port 8787), then refresh this page.
              </p>
            </div>
          </div>
        )}

        {apiReady === true && (
          <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/80 px-3 py-1.5 text-xs font-medium text-emerald-900 w-fit">
            <FiCheckCircle className="text-emerald-600" />
            Patch server reachable
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start">
          <section className="flex min-w-0 flex-col gap-3 lg:sticky lg:top-4 lg:col-span-4 lg:max-h-[calc(100vh-7rem)] lg:self-start lg:overflow-hidden xl:col-span-3">
            <div className="shrink-0 rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm backdrop-blur-sm">
              <label className="block">
                <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                  <FiCalendar className="text-slate-400" />
                  Filter from date
                </span>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-900 outline-none ring-cyan-500/20 transition focus:border-cyan-400 focus:bg-white focus:ring-2"
                />
              </label>

              <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-2">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={uploadedToCloud}
                    onChange={(e) => setUploadedToCloud(e.target.checked)}
                    className="h-4 w-4 shrink-0 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="text-xs text-slate-700">
                    <span className="font-medium text-slate-900">Uploaded to cloud</span>
                    <span className="ml-1 text-slate-500">(API: uploadedToCloud)</span>
                  </span>
                </label>
              </div>
            </div>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-sm lg:min-h-[12rem]">
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-1.5">
                  <FiLayers className="shrink-0 text-base text-indigo-600" />
                  <h2 className="truncate text-xs font-semibold text-slate-900 sm:text-sm">Script categories</h2>
                </div>
                <button
                  type="button"
                  onClick={selectAllTypes}
                  className="shrink-0 text-[11px] font-medium text-cyan-600 hover:text-cyan-800 sm:text-xs"
                >
                  Select all
                </button>
              </div>
              <ul className="grid max-h-[min(300px,50vh)] min-h-0 flex-1 grid-cols-1 gap-1.5 overflow-y-auto overscroll-contain px-2 pb-2 pt-2 sm:max-h-[min(360px,54vh)] sm:grid-cols-2 lg:max-h-none">
                {EVENT_OPTIONS.map((opt) => {
                  const on = selectedTypes.includes(opt.value);
                  return (
                    <li key={opt.value} className="min-w-0">
                      <button
                        type="button"
                        title={`${opt.hint} — ${opt.value}`}
                        onClick={() => toggleType(opt.value)}
                        className={`flex w-full min-w-0 items-center gap-2 rounded-lg border px-2 py-2 text-left transition ${
                          on
                            ? 'border-cyan-400/80 bg-cyan-50/90 shadow-sm ring-1 ring-cyan-500/15'
                            : 'border-slate-200/90 bg-white/90 hover:border-slate-300'
                        }`}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[9px] font-bold leading-none ${
                            on ? 'border-cyan-600 bg-cyan-600 text-white' : 'border-slate-300 bg-white text-slate-200'
                          }`}
                        >
                          {on ? '✓' : ''}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[11px] font-semibold leading-snug text-slate-900 sm:text-xs">
                            {opt.label}
                          </span>
                          <span className="mt-0.5 block truncate font-mono text-[9px] uppercase tracking-wide text-slate-400">
                            {opt.value}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="shrink-0 rounded-2xl border border-indigo-200/60 bg-white/95 p-1 shadow-md ring-1 ring-indigo-500/10 backdrop-blur-sm">
              <button
                type="button"
                disabled={submitting}
                onClick={onGenerate}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-600 to-indigo-600 py-3 text-sm font-semibold text-white shadow-md transition hover:from-cyan-500 hover:to-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Generating…
                  </>
                ) : (
                  <>
                    <FiCode className="text-lg" />
                    Generate SQL script
                  </>
                )}
              </button>
            </div>
          </section>

          <section className="min-w-0 lg:col-span-8 xl:col-span-9">
            <div className="sticky top-4 flex h-[min(78vh,720px)] min-h-[280px] min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-900 shadow-xl ring-1 ring-black/5">
              <div className="flex flex-col gap-3 border-b border-slate-700/80 bg-slate-800/90 px-3 py-3 md:px-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 text-slate-200">
                    <FiCode />
                    <span className="text-sm font-medium">Output</span>
                    {sqlText ? (
                      <>
                        <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[11px] text-slate-300">
                          {displayedLineCount.toLocaleString()} lines
                        </span>
                        {isEdited ? (
                          <span className="text-[10px] font-medium text-amber-300/90">edited</span>
                        ) : null}
                        {!showingFullScript && (
                          <span className="text-[10px] text-slate-500">
                            ({fullLineCount.toLocaleString()} full)
                          </span>
                        )}
                      </>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={!sqlText || !displayedSql}
                      onClick={onCopy}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-40"
                    >
                      <FiCopy /> Copy
                    </button>
                    <button
                      type="button"
                      disabled={!sqlText || !editableOutput.trim()}
                      onClick={onDownload}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500 disabled:opacity-40"
                    >
                      <FiDownload /> .sql
                    </button>
                    <button
                      type="button"
                      disabled={!sqlText || !editableOutput.trim() || apiReady !== true}
                      title={
                        apiReady !== true
                          ? 'Patch server must be running with DESK_API_KEY for blob browse'
                          : 'Pick a folder under Azure blob storage (Desk API)'
                      }
                      onClick={openUploadPicker}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-violet-500/50 bg-violet-950/80 px-3 py-1.5 text-xs font-semibold text-violet-100 hover:bg-violet-900/80 disabled:opacity-40"
                    >
                      <FiUploadCloud /> Upload to directory
                    </button>
                  </div>
                </div>
                {sqlText && parsedSections.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2 border-t border-slate-700/50 pt-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Show
                    </span>
                    {EVENT_OPTIONS.map((opt) => {
                      const inFile = keysInFile.includes(opt.value);
                      const on = outputSectionFilter[opt.value];
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={!inFile}
                          onClick={() => toggleOutputSection(opt.value)}
                          title={
                            inFile
                              ? `Toggle ${opt.label} block`
                              : 'Not present in this script'
                          }
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                            !inFile
                              ? 'cursor-not-allowed border-slate-600/50 text-slate-600 opacity-50'
                              : on
                                ? 'border-cyan-500/60 bg-cyan-950/80 text-cyan-200 shadow-sm'
                                : 'border-slate-600 bg-slate-900/80 text-slate-400 hover:border-slate-500'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="relative min-h-0 flex-1 bg-slate-950">
                {sqlText ? (
                  displayedSql ? (
                    <textarea
                      spellCheck={false}
                      autoComplete="off"
                      value={editableOutput}
                      onChange={(e) => setEditableOutput(e.target.value)}
                      aria-label="Generated SQL (editable)"
                      className="absolute inset-0 box-border h-full w-full resize-none cursor-text border-0 bg-slate-950 p-4 font-mono text-[11px] leading-relaxed text-cyan-100/95 outline-none ring-cyan-500/30 selection:bg-cyan-800/50 focus:ring-2 focus:ring-inset md:text-xs"
                      style={{ WebkitTextFillColor: 'rgba(207, 250, 254, 0.95)' }}
                    />
                  ) : (
                    <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2 p-4 text-center text-sm text-amber-200/90">
                      Turn on at least one section in <strong>Show</strong> to preview.
                    </div>
                  )
                ) : (
                  <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 px-4 text-center text-slate-500">
                    <FiCode className="text-3xl text-slate-600" />
                    Script appears here after you generate.
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {uploadPickerOpen ? (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeUploadPicker();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="blob-browse-title"
            className="flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-600 bg-slate-900 shadow-2xl ring-1 ring-white/10"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-700 px-4 py-3">
              <div className="min-w-0">
                <h2 id="blob-browse-title" className="text-sm font-semibold text-white">
                  Upload destination
                </h2>
                <p className="mt-0.5 text-[11px] leading-snug text-slate-400">
                  Same layout as Azure container <strong className="text-slate-300">fluxion</strong> — Desk{' '}
                  <code className="rounded bg-slate-800 px-1 text-[10px] text-slate-300">browse</code> +{' '}
                  <code className="rounded bg-slate-800 px-1 text-[10px] text-slate-300">upload</code> (key stays
                  on the patch server).
                </p>
              </div>
              <button
                type="button"
                onClick={closeUploadPicker}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Close"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-slate-800 px-3 py-2">
              <input
                ref={blobExtraFilesInputRef}
                type="file"
                multiple
                className="pointer-events-none fixed h-0 w-0 opacity-0"
                accept=".sql,.txt,text/plain,application/sql,*/*"
                onChange={(e) => {
                  const list = e.target.files ? Array.from(e.target.files) : [];
                  setExtraUploadFiles(list);
                }}
              />
              <button
                type="button"
                disabled={parentBrowsePrefix === undefined || blobBrowseLoading || blobUploading}
                onClick={() => {
                  if (parentBrowsePrefix === undefined) return;
                  loadBlobBrowse(parentBrowsePrefix);
                }}
                className="inline-flex items-center gap-1 rounded-md border border-slate-600 bg-slate-800 px-2.5 py-1 text-[11px] font-medium text-slate-200 hover:border-cyan-500/50 hover:text-cyan-200 disabled:opacity-40"
              >
                <FiArrowUp className="text-sm" /> Up
              </button>
              <button
                type="button"
                disabled={blobBrowseLoading || blobUploading}
                onClick={() => loadBlobBrowse(browsePrefixForNav)}
                className="inline-flex items-center gap-1 rounded-md border border-slate-600 bg-slate-800 px-2.5 py-1 text-[11px] font-medium text-slate-200 hover:border-cyan-500/50 hover:text-cyan-200 disabled:opacity-40"
              >
                <FiRefreshCw className={`text-sm ${blobBrowseLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
              <button
                type="button"
                disabled={blobBrowseLoading || blobUploading}
                onClick={() => loadBlobBrowse('')}
                className="rounded-md border border-slate-600 bg-slate-800 px-2.5 py-1 text-[11px] font-medium text-slate-200 hover:border-cyan-500/50 hover:text-cyan-200 disabled:opacity-40"
              >
                Root
              </button>
              <button
                type="button"
                disabled={blobBrowseLoading || blobUploading}
                onClick={() => loadBlobBrowse(defaultBlobBrowsePrefix())}
                className="rounded-md border border-slate-600 bg-slate-800 px-2.5 py-1 text-[11px] font-medium text-slate-200 hover:border-cyan-500/50 hover:text-cyan-200 disabled:opacity-40"
              >
                patches/
              </button>
              <button
                type="button"
                disabled={blobBrowseLoading || blobUploading || !blobBrowsePayload}
                onClick={() => blobExtraFilesInputRef.current?.click()}
                title="Append files to the next upload (multipart files field)"
                className="rounded-md border border-slate-600 bg-slate-800 px-2.5 py-1 text-[11px] font-medium text-slate-200 hover:border-cyan-500/50 hover:text-cyan-200 disabled:opacity-40"
              >
                Add files…
              </button>
              <span className="ml-auto max-w-[min(100%,220px)] truncate text-[10px] text-slate-500">
                {blobBrowsePayload?.containerName ?? '—'} /{' '}
                <span className="text-slate-400">{browsePrefixForNav || '(root)'}</span>
              </span>
            </div>

            <div className="shrink-0 border-b border-slate-800 px-4 py-2">
              <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-[11px]">
                {(blobBrowsePayload?.breadcrumbs || []).map((crumb, i, arr) => (
                  <span key={`${crumb.prefix}-${crumb.label}`} className="flex items-center gap-1">
                    {i > 0 ? <FiChevronRight className="text-slate-600" /> : null}
                    <button
                      type="button"
                      disabled={blobBrowseLoading || blobUploading}
                      onClick={() => loadBlobBrowse(crumb.prefix ?? '')}
                      className={`max-w-[140px] truncate rounded px-1.5 py-0.5 font-medium hover:bg-slate-800 hover:text-cyan-200 disabled:opacity-40 ${
                        i === arr.length - 1 ? 'text-cyan-300' : 'text-slate-300'
                      }`}
                    >
                      {crumb.label || '(root)'}
                    </button>
                  </span>
                ))}
              </nav>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
              {blobBrowseLoading ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-slate-400">
                  <span className="h-8 w-8 animate-spin rounded-full border-2 border-slate-600 border-t-cyan-400" />
                  Loading…
                </div>
              ) : blobBrowseError ? (
                <div className="rounded-xl border border-amber-500/40 bg-amber-950/40 px-3 py-3 text-sm text-amber-100">
                  <p className="font-semibold text-amber-200">Could not list directory</p>
                  <p className="mt-1 text-xs text-amber-100/90">{blobBrowseError}</p>
                  <p className="mt-2 text-[11px] text-amber-200/70">
                    Ensure <code className="rounded bg-black/30 px-1">DESK_API_KEY</code> is set on the
                    patch server and restart <code className="rounded bg-black/30 px-1">npm run server</code>.
                  </p>
                </div>
              ) : (
                <ul className="space-y-0.5">
                  {sortedBlobDirs.map((dir) => (
                    <li key={dir.prefix || dir.name}>
                      <button
                        type="button"
                        disabled={blobUploading}
                        onClick={() => loadBlobBrowse(dir.prefix || `${blobBrowsePrefix}${dir.name}/`)}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-slate-200 hover:bg-slate-800/90 disabled:opacity-40"
                      >
                        <FiFolder className="text-lg shrink-0 text-amber-400/90" />
                        <span className="min-w-0 flex-1 truncate font-medium">{dir.name}</span>
                        <FiChevronRight className="shrink-0 text-slate-500" />
                      </button>
                    </li>
                  ))}
                  {sortedBlobFiles.map((file) => (
                    <li key={file.blobPath || file.name}>
                      <div className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-400">
                        <FiFile className="text-lg shrink-0 text-slate-500" />
                        <span className="min-w-0 flex-1 truncate">{file.name}</span>
                        {file.sizeBytes != null ? (
                          <span className="shrink-0 text-[10px] text-slate-600">
                            {(Number(file.sizeBytes) / 1024).toFixed(1)} KB
                          </span>
                        ) : null}
                        {file.url ? (
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 rounded p-1 text-cyan-400 hover:bg-slate-800 hover:text-cyan-300"
                            title="Open in new tab"
                          >
                            <FiExternalLink className="text-base" />
                          </a>
                        ) : null}
                      </div>
                    </li>
                  ))}
                  {sortedBlobDirs.length === 0 && sortedBlobFiles.length === 0 && !blobBrowseLoading ? (
                    <li className="py-12 text-center text-sm text-slate-500">This folder is empty.</li>
                  ) : null}
                </ul>
              )}
            </div>

            <div className="shrink-0 border-t border-slate-700 bg-slate-950/80 px-4 py-3">
              <label className="flex cursor-pointer items-center gap-2 text-[11px] text-slate-300">
                <input
                  type="checkbox"
                  checked={includeScriptInUpload}
                  onChange={(e) => setIncludeScriptInUpload(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-500 text-cyan-500 focus:ring-cyan-500/40"
                />
                Include generated .sql as{' '}
                <code className="text-[10px] text-slate-400">patch.sql</code> (download still uses{' '}
                <code className="text-[10px] text-slate-400">
                  migration-{filterDate}
                  {`${showingFullScript ? '' : '-filtered'}${isEdited ? '-edited' : ''}`}.sql
                </code>
                )
              </label>
              {extraUploadFiles.length > 0 ? (
                <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500">
                  Also uploading:{' '}
                  <span className="text-slate-400">
                    {extraUploadFiles.map((f) => f.name).join(', ')}
                  </span>
                </p>
              ) : null}
              <p className="mt-1 text-[10px] text-slate-600">
                <span className="text-slate-500">POST</span>{' '}
                <code className="text-slate-500">/api/storage/blob/upload</code> — field{' '}
                <code className="text-slate-500">prefix</code> (current folder) + one or more{' '}
                <code className="text-slate-500">files</code>.
              </p>
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={closeUploadPicker}
                  className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-800"
                >
                  Close
                </button>
                <button
                  type="button"
                  disabled={blobUploading || !blobBrowsePayload || !hasBlobUploadPayload}
                  onClick={onBlobUpload}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-cyan-600 px-3 py-1.5 text-xs font-semibold text-white shadow-md hover:from-violet-500 hover:to-cyan-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {blobUploading ? (
                    <>
                      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <FiUploadCloud className="text-sm" />
                      Upload to current folder
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
